import { z } from 'zod';
import { EnrollmentStatus } from './campaigns.js';
import { ContactKind } from './contact-words.js';
import { SfObject } from './crm.js';
import { DoNotContactCategory } from './review.js';
/** What the research step reads from Salesforce for one lead. */
export const ResearchSource = z.enum(['record', 'related', 'tasks', 'events', 'notes', 'content_notes', 'emails', 'chatter', 'chatter_comments']);
/** ok; missing = the org has no such object or field; denied = the integration user may not read it; error = another refusal; skipped = not attempted. */
export const ResearchSourceStatus = z.enum(['ok', 'missing', 'denied', 'error', 'skipped']);
export const ResearchSourceSummary = z.object({
    source: ResearchSource,
    status: ResearchSourceStatus,
    count: z.number().int().min(0),
    /** More items existed than the cap kept (the most recent are kept). */
    truncated: z.boolean(),
    /** Salesforce's error code, or a short reason; never record content. */
    note: z.string().max(300).nullable(),
});
/** The record's AI call consent field (`AI_Call_Consent__c` through the field map) when researched.
 * 'unknown': the field is configured but its value could not be read; never treated as consent. */
export const AiConsentStatus = z.enum(['yes', 'no', 'field_missing', 'unknown']);
/** campaign_enrollments.call_stage. */
export const CallStage = z.enum(['research', 'review', 'approved', 'queued', 'done']);
export const EvidenceSource = z.enum(['record', 'related', 'task', 'event', 'note', 'email', 'chatter']);
export const SellingSignal = z.object({
    signal: z.string().trim().min(1).max(200),
    /** Words copied from the data that show the signal. */
    evidence: z.string().trim().min(1).max(300),
    source: EvidenceSource,
    strength: z.enum(['strong', 'moderate', 'weak']),
});
/** The four things every call tries to learn. */
export const CallGoalKey = z.enum(['still_selling', 'timeline', 'condition', 'price_expectations']);
export const CALL_GOAL_KEYS = CallGoalKey.options;
export const CallGoal = z.object({
    goal: CallGoalKey,
    /** What the records already say about it, or null. */
    known: z.string().trim().max(300).nullable(),
    /** How the call should find out. */
    approach: z.string().trim().min(1).max(300),
});
/** Recipient-local part of the 08:00–21:00 calling window the call should aim for. */
export const PreferredWindow = z.enum(['any', 'morning', 'afternoon', 'evening']);
/** What a seller conversation should learn; `stillToLearn` lists those Salesforce does not answer yet (plan 1D). */
export const QualificationTopic = z.enum(['motivation', 'timeline', 'condition', 'repairs', 'occupancy', 'price', 'competition', 'mortgage', 'decision_makers']);
export const QUALIFICATION_TOPICS = QualificationTopic.options;
/** A returning seller: when we last spoke and about what (plan 1D). */
export const Reengagement = z.object({
    /** Computed words ("back in February"), never digits; overwritten after parsing (Task 9). */
    lastContact: z.string().trim().min(1).max(80).nullable(),
    /**
     * Fix 1 (M-4): when the contact was and what kind it was, set by our system (never the model or a person). The words
     * are worked out from the date when the plan is read (contact-words.ts); plans stored before Fix 1 have neither.
     */
    lastContactAt: z.string().datetime({ offset: true }).nullable().optional(),
    lastContactKind: ContactKind.nullable().optional(),
    lastTopic: z.string().trim().min(1).max(200).nullable(),
});
/** The longest first text a plan may draft (owner, 2026-10-19: "a plan for a SMS (< 140 char)"). */
export const TEXT_MESSAGE_MAX = 140;
const lines = (maxChars, maxItems, minItems = 0) => z.array(z.string().trim().min(1).max(maxChars)).min(minItems).max(maxItems);
/** The plan model's output (zod-validated before use) and what a person approves. */
export const CallPlan = z.object({
    situationSummary: z.string().trim().min(1).max(800),
    sellingSignals: z.array(SellingSignal).max(8),
    /** What to say right after the AI disclosure and once they agree to a minute. */
    opener: z.string().trim().min(1).max(300),
    goals: z
        .array(CallGoal)
        .length(4)
        .refine((goals) => new Set(goals.map((g) => g.goal)).size === CALL_GOAL_KEYS.length, { message: 'Each goal exactly once' }),
    talkingPoints: lines(200, 8),
    questions: lines(200, 10, 1),
    avoid: lines(200, 8),
    bestTimeToCall: z.object({ window: PreferredWindow, reason: z.string().trim().max(200) }),
    /** Non-null holds the person in Needs Review; no plan is offered for approval. */
    doNotContact: z.object({ category: DoNotContactCategory, quote: z.string().trim().min(1).max(300) }).nullable(),
    /** Plan 1D. The defaults let plans stored before 1D (call_plans.plan) still parse. */
    reengagement: Reengagement.nullable().default(null),
    stillToLearn: z.array(QualificationTopic).max(9).default([]),
    /**
     * A first text message to the person, drafted with the call plan (null: no text should go to them). Optional, so
     * plans stored before it parse unchanged. A draft for a person to read: nothing sends it.
     */
    textMessage: z.string().trim().min(1).max(TEXT_MESSAGE_MAX).nullable().optional(),
});
/** What a person may edit: everything but the do-not-contact assessment. */
export const EditableCallPlan = CallPlan.omit({ doNotContact: true });
export const GateWarningCode = z.enum([
    'no_ai_consent',
    'consent_field_missing',
    /** The consent field is configured but its value could not be read (CF-5): never consent. */
    'consent_unknown',
    'no_phone',
    'opted_out',
    'blocked',
    'dnc',
    /** A do-not-contact flag on the person is waiting in Needs Review (CF-10). */
    'dnc_pending',
    /** The plan was flagged do-not-contact and no person has dismissed the flag (CF-10). */
    'dnc_not_dismissed',
    /** The plan's text would be refused by the voice agent's check (a price, an offer, a URL, a human claim...): edit it. */
    'plan_text_rejected',
    'sf_do_not_call',
    'skip_on_dialer',
    'closed',
    'state_daily_cap',
    'outside_calling_hours',
]);
/** block = the engine will refuse the call as things stand; info = it may delay it. */
export const GateWarning = z.object({ code: GateWarningCode, severity: z.enum(['block', 'info']), words: z.string() });
export const CallPlanVersion = z.object({
    version: z.number().int().min(1),
    status: z.enum(['proposed', 'approved']),
    source: z.enum(['model', 'edit']),
    plan: EditableCallPlan,
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
    /** The model raised do-not-contact and a person dismissed it before this card was shown. */
    dncFlagDismissed: z.boolean(),
    /** Who dismissed it and when (display name or email); null when it was not recorded. */
    dncFlagDismissedBy: z.string().nullable(),
    dncFlagDismissedAt: z.string().nullable(),
});
/** One lead on the call plan board. */
export const CallPlanCard = z.object({
    enrollmentId: z.string().uuid(),
    sfObject: SfObject,
    sfRecordId: z.string(),
    /** `${instance_url}/${sfRecordId}`; null when the connection is gone. */
    recordUrl: z.string().url().nullable(),
    name: z.string().nullable(),
    ownerName: z.string().nullable(),
    enrollmentStatus: EnrollmentStatus,
    callStage: CallStage,
    consent: AiConsentStatus.nullable(),
    warnings: z.array(GateWarning),
    research: z.object({ version: z.number(), collectedAt: z.string(), sources: z.array(ResearchSourceSummary) }).nullable(),
    plan: CallPlanVersion.nullable(),
    /** Last research or plan failure, in plain words. */
    prepareError: z.string().nullable(),
    /** The viewer is the record owner or an admin (review.ts's rule). */
    mayDecide: z.boolean(),
});
/** GET /api/campaigns/:id/call-plans — 25 cards a page; `counts` covers the whole campaign (active enrollments). */
export const CallPlansResponse = z.object({
    cards: z.array(CallPlanCard),
    nextCursor: z.string().nullable(),
    counts: z.record(CallStage, z.number()),
});
/** POST /api/call-plans/:enrollmentId/approve — `version` is the one the person read. */
export const ApproveCallPlanRequest = z.object({ version: z.number().int().min(1) });
/** PUT /api/call-plans/:enrollmentId — saves a new version (status proposed) based on `version`. */
export const EditCallPlanRequest = z.object({ version: z.number().int().min(1), plan: EditableCallPlan });
/**
 * POST /api/campaigns/:id/ai-calls/release — "Call all approved". `skipped` counts the leads looked at and not released.
 * `more`: the call stopped at its cap (calls released, or pages read) with approved leads still unread; run it again.
 */
export const ReleaseCallsResponse = z.object({ released: z.number(), skipped: z.number(), more: z.boolean() });
//# sourceMappingURL=call-plans.js.map